import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, FolderOpen, FileUp, ChevronDown, ShieldCheck } from "lucide-react";
import { IMAGES } from "../../constants/content";
import { EASE } from "../../components/Reveal";
import { useSite } from "../../context/SiteContext";

const HEADLINE = ["Quality Residential", "Building Across", "Wellington"];

const TRUST = ["Licensed Building Practitioner", "10+ Years Experience", "Fully Insured", "Wellington Residential Builders"];

export default function Hero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { images } = useSite();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <>
      <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden bg-ink">
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <img src={images.hero} alt="Modern New Zealand home built by Wellington residential builders" className="w-full h-full object-cover img-duotone" fetchPriority="high" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/60 via-transparent to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 w-full pb-20 md:pb-28 pt-40">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="flex items-center gap-3 font-mono text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-white mb-6"
            data-testid="hero-eyebrow"
          >
            <span className="w-8 h-[3px] bg-gold inline-block shrink-0" />
            RazorBuild – Proudly Serving Greater Wellington
          </motion.p>
          <h1 className="font-display font-bold text-white tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-7xl display-expanded max-w-4xl" data-testid="hero-headline">
            {HEADLINE.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.25 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-6 text-white/75 text-base md:text-lg leading-relaxed max-w-xl"
            data-testid="hero-subtext"
          >
            From smaller home improvements to renovations, extensions and complete new builds, Razor Build provides quality residential building services across Wellington.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link to="/contact" data-testid="hero-start-project" className="inline-flex items-center gap-2.5 bg-gold hover:bg-golddark text-white font-mono text-xs tracking-[0.18em] uppercase px-6 py-4 transition-colors">
              Start Your Project <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/projects" data-testid="hero-view-projects" className="inline-flex items-center gap-2.5 border border-white/40 hover:border-white text-white font-mono text-xs tracking-[0.18em] uppercase px-6 py-4 transition-colors">
              <FolderOpen className="w-4 h-4" /> View Our Projects
            </Link>
            <button onClick={() => navigate("/contact#upload")} data-testid="hero-send-plans" className="inline-flex items-center gap-2.5 text-white/80 hover:text-gold font-mono text-xs tracking-[0.18em] uppercase px-2 py-4 transition-colors">
              <FileUp className="w-4 h-4" /> Send Your Plans
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2"
          >
            <p className="text-sm text-white/60" data-testid="hero-friendly-line">Got a smaller job? We'd still be happy to hear from you.</p>
            <a href="#urgent" data-testid="hero-urgent-link" className="inline-flex items-center gap-2 text-sm text-[#FF8A7A] hover:text-[#FF6B5E] transition-colors">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B5E] animate-pulse" /> Property damaged? Get urgent building help
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-6 right-6 md:right-10 z-10 hidden md:flex flex-col items-center gap-2 text-white/50"
        >
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ writingMode: "vertical-rl" }}>Scroll</span>
          <ChevronDown className="w-4 h-4 animate-scroll-cue" />
        </motion.div>
      </section>

      <div data-testid="trust-bar" className="bg-charcoal border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-5 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
          {TRUST.map((item, i) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
              className="flex items-center gap-2.5"
              data-testid={`trust-item-${i}`}
            >
              <ShieldCheck className="w-4 h-4 text-gold shrink-0" />
              <span className="font-mono text-[10px] md:text-[11px] tracking-[0.14em] uppercase text-white/80">{item}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </>
  );
}
